import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Shield } from 'lucide-react';

interface WinProtectControlsProps {
  enabled: boolean;
  startWins: number;
  maxWins: number;
  includeNthWin: boolean;
  mode: 'consecutive_trades' | 'consecutive_losses';
  onEnabledChange: (enabled: boolean) => void;
  onStartWinsChange: (startWins: number) => void;
  onMaxWinsChange: (maxWins: number) => void;
  onIncludeNthWinChange: (includeNthWin: boolean) => void;
  onModeChange: (mode: 'consecutive_trades' | 'consecutive_losses') => void;
}

export const WinProtectControls = ({
  enabled,
  startWins,
  maxWins,
  includeNthWin,
  mode,
  onEnabledChange,
  onStartWinsChange,
  onMaxWinsChange,
  onIncludeNthWinChange,
  onModeChange,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield size={18} />
          Win Protect
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="winProtectEnabled"
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
          <Label htmlFor="winProtectEnabled">
            Enable win protect
          </Label>
        </div>
        
        {enabled && (
          <div className="space-y-4 pl-6 border-l-2 border-muted">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Stop Mode</Label>
              <RadioGroup value={mode} onValueChange={onModeChange}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_trades" id="mode-trades" />
                  <Label htmlFor="mode-trades" className="text-sm">
                    Stop after M trades (any outcome)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consecutive_losses" id="mode-losses" />
                  <Label htmlFor="mode-losses" className="text-sm">
                    Stop after M consecutive losses
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start after N wins</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={startWins}
                  onChange={(e) => onStartWinsChange(parseInt(e.target.value) || 1)}
                  className="w-full mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Wait for this many consecutive wins before starting
                </p>
              </div>
              
              <div>
                <Label>Stop after M {mode === 'consecutive_trades' ? 'trades' : 'losses'}</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={maxWins}
                  onChange={(e) => onMaxWinsChange(parseInt(e.target.value) || 1)}
                  className="w-full mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {mode === 'consecutive_trades' 
                    ? 'Stop trading after this many trades (wins or losses)' 
                    : 'Stop trading after this many consecutive losses'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeNthWin"
                checked={includeNthWin}
                onCheckedChange={onIncludeNthWinChange}
              />
              <Label htmlFor="includeNthWin" className="text-sm">
                Include the Mth winning trade in results
              </Label>
            </div>
            
            {!includeNthWin && (
              <p className="text-xs text-muted-foreground pl-6">
                ✓ The Mth winning trade will be excluded from results (trade but don't count)
              </p>
            )}
            
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
              <strong>How it works:</strong>
              <br />
              • <strong>WAITING:</strong> Skip all trades until {startWins} consecutive wins, including the {startWins}th win itself
              • <strong>OPEN:</strong> Start trading from the trade after the {startWins}th win, then {mode === 'consecutive_trades' ? `take ${maxWins} trades` : `continue until ${maxWins} consecutive losses`}
              • <strong>PAUSED:</strong> Skip all trades until a loss occurs, then reset to WAITING
              • Any loss in WAITING or OPEN states resets to WAITING (losses are skipped in WAITING, included in OPEN)
              • The {startWins}th win is always skipped to ensure trading starts "after" reaching {startWins} wins
              • The {maxWins}th {mode === 'consecutive_trades' ? 'trade' : 'loss'} can be included or excluded based on the toggle setting
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};